import React from 'react';
import ReactDOM from 'react-dom/client';
import reportWebVitals from './reportWebVitals';
import {
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";

import { Provider } from 'react-redux'
import { RootState, store } from './store'

import App from './App';
import Home from './pages/home/home';
import Bestekken from './pages/bestekken/bestekken';
import Hoofdcodes from './pages/hoofdcodes/hoofdcodes';
import HoofdcodesList from './pages/hoofdcodes/list/list';
import ErrorPage from './pages/error/error';

import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/overrides.css';
import BestekDetail from './pages/bestekken/detail/bestek_detail';
import BestekkenListPage from './pages/bestekken/list/list';
import Ovks from './pages/ovks/ovks';
import OvkList from './pages/ovks/list/list';
import OvkDetail from './pages/ovks/detail/detail';
import Statistics from './pages/statistics/statistics';
import OvkStatistics from './pages/statistics/ovk-statistics/ovk-statistics';
import HoofdcodeDetail from './pages/hoofdcodes/detail/detail';
import { AuthenticationResult, EventMessage, EventType, PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './config/auth';
import { MsalProvider } from '@azure/msal-react';
import { authActions } from './store/reducers/auth';
import apiService from './api-service';
import { messageActions } from './store/reducers/messages';
import WordCountList from './pages/word-count/list';
import BepalingenList from './pages/bepalingen/list/bepalingen-list';
import BepalingDetail from './pages/bepalingen/detail/bepaling-detail';
import Bepalingen from './pages/bepalingen/bepalingen';
import LidDetail from './pages/bepalingen/detail/lid-detail'; // NIEUW: Import lid detail

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "",
        element: <Home />
      },
      {
        path: "bestekken",
        element: <Bestekken />,
        children: [
          {
            path: "",
            element: <BestekkenListPage />
          },
          {
            path: ":id/:reg",
            element: <BestekDetail />
          },
        ]
      },
      {
        path: "hoofdcodes",
        element: <Hoofdcodes />,
        children: [
          {
            path: '',
            element: <HoofdcodesList />
          },
          {
            path: ':id',
            element: <HoofdcodeDetail />
          }
        ]
      },
      // UPDATED: Add lid route to bepalingen
      {
        path: "bepalingen",
        element: <Bepalingen />,
        children: [
          {
            path: '',
            element: <BepalingenList />
          },
          {
            path: ':id',
            element: <BepalingDetail />
          },
          // NIEUW: Voeg deze route toe voor individuele leden
          {
            path: 'lid/:id',
            element: <LidDetail />
          }
        ]
      },
      {
        path: "ovks",
        element: <Ovks />,
        children: [
          {
            path: "",
            element: <OvkList />
          },
          {
            path: ":id/:nummer/:naam",
            element: <OvkDetail />
          }
        ]
      },
      {
        path: "statistics",
        element: <Statistics />,
        children: [
          {
            path: 'ovks',
            element: <OvkStatistics />
          }
        ]
      },
      {
        path: 'word-count',
        element: <WordCountList />
      }
    ]
  },
], { basename: process.env.PUBLIC_URL });

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

export const msalInstance = new PublicClientApplication(msalConfig);

const handleAuthenticationResult = (result: AuthenticationResult) => {
  msalInstance.setActiveAccount(result.account);

  const state: RootState = store.getState();
  
  if (result.accessToken !== state.auth.azure_token && !state.auth.fetching_crow_token) {
    store.dispatch(authActions.signin({ method: 'azure', token: result.accessToken }));
    store.dispatch(authActions.setCrowTokenFetching(true));
    
    apiService.post<{ token: string, name: string }>('/auth/trade-azure-token/', { azure_token: result.accessToken })
        .then((response) => {
          if (state.auth.token === null) {
            store.dispatch(messageActions.add({ title: "Ingelogd", body: `Welkom terug ${response.data.name}!` }));
          } else {
            console.log("Refreshed token");
          }
          store.dispatch(authActions.signin({ method: 'crow', token: response.data.token }));
          
        }, (e) => {
          store.dispatch(messageActions.add({ title: "Inloggen mislukt", body: "Er is iets fout gegaan tijdens het inlogproces, probeer het a.u.b. later nog eens." }));
          store.dispatch(authActions.signout());
        }).finally(() => store.dispatch(authActions.setCrowTokenFetching(false)));
  }
}

msalInstance.initialize().then(() => {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    // @ts-ignore
    if ([EventType.LOGIN_SUCCESS, EventType.ACQUIRE_TOKEN_SUCCESS].includes(event.eventType) && event.payload) {
      const payload = event.payload as AuthenticationResult;
      handleAuthenticationResult(payload);
    }
  });

  root.render(
    <Provider store={store}>
      <MsalProvider instance={msalInstance}>
        <RouterProvider router={router} />
      </MsalProvider>
    </Provider>
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();